// src/components/album/MediaUploadModal.jsx

import React, { useState, useCallback } from 'react';
import { X, Upload, Film, AlertCircle, CheckCircle } from 'lucide-react';
import { albumService } from '../../services/albumService';
import { useAuth } from '../../contexts/AuthContext';

export const MediaUploadModal = ({
  bookId,
  galleryId,
  isApprovedContributor,
  onClose,
  onUploadComplete
}) => {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    const newFiles = selectedFiles.map(file => ({
      id: Math.random().toString(36),
      file,
      preview: URL.createObjectURL(file),
      progress: 0,
      status: 'pending'
    }));

    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const removeFile = (id) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter(f => f.id !== id);
    });
  };

  const uploadFile = async (uploadFile) => {
    try {
      setFiles(prev =>
        prev.map(f =>
          f.id === uploadFile.id ? { ...f, status: 'uploading', progress: 50 } : f
        )
      );

      await albumService.uploadUserMedia(
        bookId,
        galleryId,
        uploadFile.file,
        user.uid,
        user.email,
        user.displayName || user.email,
        isApprovedContributor
      );

      setFiles(prev =>
        prev.map(f =>
          f.id === uploadFile.id ? { ...f, status: 'complete', progress: 100 } : f
        )
      );
    } catch (error) {
      setFiles(prev =>
        prev.map(f =>
          f.id === uploadFile.id ? { ...f, status: 'error', error: error.message } : f
        )
      );
      throw error;
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);

    try {
      for (const file of files) {
        if (file.status === 'pending') {
          await uploadFile(file);
        }
      }

      setTimeout(() => {
        onUploadComplete();
      }, 1500);
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const allComplete = files.length > 0 && files.every(f => f.status === 'complete');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl bg-white rounded-xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold">Upload Media</h2>
            <p className="text-sm text-gray-600 mt-1">
              {isApprovedContributor
                ? 'Your uploads will be immediately visible'
                : 'Your uploads will be reviewed before appearing in the gallery'}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={uploading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {!uploading && (
            <label className="block mb-6">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-blue-500 hover:bg-blue-50/50 transition-colors cursor-pointer">
                <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-lg font-medium mb-2">
                  Click to select files or drag and drop
                </p>
                <p className="text-sm text-gray-600">Images and videos supported</p>
              </div>
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFileSelect}
                className="hidden"
                disabled={uploading}
              />
            </label>
          )}

          {files.length > 0 && (
            <div className="space-y-3">
              {files.map(file => (
                <div key={file.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 w-16 h-16 bg-gray-200 rounded overflow-hidden">
                    {file.file.type.startsWith('image/') ? (
                      <img src={file.preview} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{file.file.name}</p>
                    <p className="text-sm text-gray-600">
                      {(file.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>

                    {file.status === 'uploading' && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${file.progress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {file.status === 'error' && (
                      <p className="text-sm text-red-600 mt-1">{file.error}</p>
                    )}
                  </div>

                  <div className="flex-shrink-0">
                    {file.status === 'complete' && <CheckCircle className="h-6 w-6 text-green-600" />}
                    {file.status === 'error' && <AlertCircle className="h-6 w-6 text-red-600" />}
                    {file.status === 'pending' && !uploading && (
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {files.length > 0 && (
              <span>
                {files.filter(f => f.status === 'complete').length} of {files.length} uploaded
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={uploading}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            
            
            >
              {allComplete ? 'Close' : 'Cancel'}
            </button>
            {!allComplete && (
              <button
                onClick={handleUpload}
                disabled={files.length === 0 || uploading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                <Upload className="h-4 w-4" />
                <span>{uploading ? 'Uploading...' : 'Upload'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};